import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from "@angular/core";
import { DataTypeModelKey } from "app/models/dataType.model";
import { DeviceAggregatedData, DeviceDataModel, DeviceDataType } from "app/models/deviceData.model";
import { ProjectDeviceModel } from "app/models/projectDevice.model";
import { DataService } from "app/services/api/data.service";
import { ProjectDeviceService } from "app/services/api/projectDevice.service";
import { ToastrService } from "ngx-toastr";
import { Subscription } from "rxjs";
import * as Highcharts from "highcharts";
import { chartBandSpectraCard } from "app/chart-config/chart-band-spectra-card";
import { DeviceMqttService } from "app/services/api/device.mqtt.service";
import { IMqttMessage } from "ngx-mqtt";
import { ContextService } from "app/services/context.service";
import { chartBandSpectraSimple } from "app/chart-config/chart-band-spectra-simple";
import {
    getSpectrumIndexesForDevice,
    hasRenderableSpectrumValues,
    selectSpectrumCategories,
    selectSpectrumSeries,
} from "app/shared/spectrum.util";

@Component({
    selector: "device-band-spectra",
    templateUrl: "./device-band-spectra.component.html",
    styleUrls: ["./device-band-spectra.component.scss"],
    standalone: false,
})
export class DeviceBandSpectra implements OnInit, OnDestroy {
    @Input() device: ProjectDeviceModel = {
        connected: true,
        lastData: null,
    } as unknown as ProjectDeviceModel;
    @Input() showDeviceLink = true;
    @Input() editGraphViews = false;
    @Input() dataType: DataTypeModelKey = DataTypeModelKey.spectrum13Octave;
    loadingData = false;
    deviceData: DeviceDataModel[];
    sensorGraphData: any = null;
    Highcharts: typeof Highcharts = Highcharts;
    chart = JSON.parse(JSON.stringify(chartBandSpectraCard));
    chartFull = JSON.parse(JSON.stringify(chartBandSpectraCard));
    chartSimple = JSON.parse(JSON.stringify(chartBandSpectraSimple));
    isKioskMode = false;
    chartRef;

    subsInstant: Subscription;
    subsBucket: Subscription;

    constructor(
        private readonly dataService: DataService,
        private readonly projectDeviceService: ProjectDeviceService,
        private readonly deviceMqtt: DeviceMqttService,
        private toastr: ToastrService,
        public readonly contextService: ContextService,
        private readonly cdr: ChangeDetectorRef,
    ) {}

    ngOnInit() {
        if (!this.device?.id) {
            return;
        }
        this.refreshData();
        this.subscribeToTopics();

        this.contextService.onKioskModeChange.subscribe((isKioskMode) => {
            this.isKioskMode = isKioskMode;
            if (this.isKioskMode) {
                this.chart.options.chart.height = "85%";
            } else {
                this.chart.options.chart.height = 280;
            }
            this.chart.update = true;

            for (let i = 250; i <= 2500; i += 250) {
                setTimeout(() => {
                    this.chart.update = true;
                    this.chartRef?.reflow();
                    window.dispatchEvent(new Event("resize"));
                }, i);
            }
        });
    }

    ngOnDestroy(): void {
        this.subsInstant?.unsubscribe();
        this.subsBucket?.unsubscribe();
    }

    chartCallback: Highcharts.ChartCallbackFunction = (chart) => {
        this.chartRef = chart;
        this.chartRef.reflow();
    };

    private subscribeToTopics() {
        this.subsInstant = this.deviceMqtt
            .deviceTopic(this.device.id, DeviceDataType.instant, this.dataType)
            .subscribe((data: IMqttMessage) => {
                const item = JSON.parse(data.payload.toString());
                this.device.lastData[this.getSpectrumFieldKey()] = item;
                this.cdr.detectChanges();
            });

        this.subsBucket = this.deviceMqtt
            .deviceTopic(this.device.id, DeviceDataType.bucket, this.dataType)
            .subscribe((data: IMqttMessage) => {
                const item = JSON.parse(data.payload.toString());

                this.deviceData[0] = item;
                this.createSensorData();
                this.cdr.detectChanges();
            });
    }

    private refreshData() {
        this.fetchDevice();
    }

    private fetchDevice() {
        if (this.loadingData) {
            return;
        }
        this.loadingData = true;

        // Fetch device
        this.projectDeviceService.get(this.device.id).subscribe(
            (data: ProjectDeviceModel) => {
                this.device = data;
                this.createSensorData();
                this.cdr.detectChanges();
            },
            (error) => {
                console.log(error);
                this.toastr.error("Error: " + error.message);
            },
        );

        // Fetch last bucket of data
        this.dataService.getDeviceDataSample(this.device.id, [this.dataType], 1, true).subscribe(
            (data: DeviceAggregatedData) => {
                this.deviceData = data[this.dataType];
                this.createSensorData();
                this.loadingData = false;
                this.cdr.detectChanges();
            },
            (error) => {
                console.log(error);
                this.toastr.error("Error: " + error.message);
                this.loadingData = false;
            },
        );
    }

    private createSensorData() {
        if (!this.deviceData) return;

        const averageSpectrum = this.deviceData?.[0]?.aggregation?.average;
        const hasAverageSpectrum = hasRenderableSpectrumValues(averageSpectrum);
        const spectrumData = this.device.lastData?.[this.getSpectrumFieldKey()];
        const spectrumIndexes = getSpectrumIndexesForDevice(this.device?.baseDeviceData?.model, hasAverageSpectrum ? 34 : 31);
        const baseCategories = [...((hasAverageSpectrum ? this.chartFull : this.chartSimple).options.xAxis.categories ?? [])];
        const nextCategories = selectSpectrumCategories(baseCategories, spectrumIndexes);

        //Process data to set it ready for drawing the graph
        //On some devices the average is not calculated, so we don't show it on the graph
        if (hasAverageSpectrum) {
            if (this.chart.options.series.length < 2) {
                this.chart.options = this.chartFull.options;
            }
            const averageSeries = (averageSpectrum ?? []).map((data, i) => {
                if (i > 30) {
                    return { y: data, color: "#0C65E8" };
                }

                return data;
            });
            const instantSeries = (spectrumData?.value ?? []).map((data, i) => {
                if (i > 30) {
                    return { y: data, color: "#0C65E877" };
                }

                return data;
            });
            this.chart.options.series[0].data = selectSpectrumSeries(averageSeries, spectrumIndexes);
            this.chart.options.series[1].data = selectSpectrumSeries(instantSeries, spectrumIndexes);
        } else {
            if (this.chart.options.series.length > 1) {
                this.chart.options = this.chartSimple.options;
            }
            this.chart.options.series[0].data = selectSpectrumSeries(spectrumData?.value ?? [], spectrumIndexes);
        }
        this.chart.options.xAxis.categories = nextCategories;

        if (this.isKioskMode) {
            this.chart.options.chart.height = "85%";
        } else {
            this.chart.options.chart.height = 280;
        }

        if (this.chartRef?.xAxis?.[0]) {
            this.chartRef.xAxis[0].setCategories(nextCategories, false);
        }
        this.chart.update = true;
        if (this.chart.visible == false) this.chart.visible = true;
        this.cdr.detectChanges();
    }

    private getSpectrumFieldKey(): "spectrum13Octave" | "spectrum13Octave1Minute" {
        return this.dataType === DataTypeModelKey.spectrum13Octave1Minute ? "spectrum13Octave1Minute" : "spectrum13Octave";
    }
}
